// src/pages/admin/components/SpecializedPropertyMediaManager.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn, Badge } from '../ui';
import MediaPickerModal from './MediaPickerModal';
import { normalizeMediaUrl } from '../../../api/base';

export default function SpecializedPropertyMediaManager({ propertyId, propertyType }) {
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [openPicker, setOpenPicker] = React.useState(false);
  const [dragId, setDragId] = React.useState(null);

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPropertyMedia,
          attach: AdminAPI.attachForeignPropertyMedia,
          update: AdminAPI.updateForeignPropertyMedia,
          delete: AdminAPI.deleteForeignPropertyMedia,
          setMain: AdminAPI.setMainForeignPropertyMedia,
          reorder: AdminAPI.reorderForeignPropertyMedia,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPropertyMedia,
          attach: AdminAPI.attachSuburbanPropertyMedia,
          update: AdminAPI.updateSuburbanPropertyMedia,
          delete: AdminAPI.deleteSuburbanPropertyMedia,
          setMain: AdminAPI.setMainSuburbanPropertyMedia,
          reorder: AdminAPI.reorderSuburbanPropertyMedia,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPropertyMedia,
          attach: AdminAPI.attachCommercialRentPropertyMedia,
          update: AdminAPI.updateCommercialRentPropertyMedia,
          delete: AdminAPI.deleteCommercialRentPropertyMedia,
          setMain: AdminAPI.setMainCommercialRentPropertyMedia,
          reorder: AdminAPI.reorderCommercialRentPropertyMedia,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPropertyMedia,
          attach: AdminAPI.attachParkingPropertyMedia,
          update: AdminAPI.updateParkingPropertyMedia,
          delete: AdminAPI.deleteParkingPropertyMedia,
          setMain: AdminAPI.setMainParkingPropertyMedia,
          reorder: AdminAPI.reorderParkingPropertyMedia,
        };
      default:
        return {
          list: AdminAPI.listPropertyMedia,
          attach: AdminAPI.attachPropertyMedia,
          update: AdminAPI.updatePropertyMedia,
          delete: AdminAPI.deletePropertyMedia,
          setMain: AdminAPI.setMainPropertyMedia,
          reorder: AdminAPI.reorderPropertyMedia,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const load = React.useCallback(async ()=>{
    if (busy) return; // Prevent concurrent requests
    setBusy(true);
    setMsg('');
    try{
      const resp = await api.list(propertyId);
      // Backend returns array directly, not nested in 'images' property
      const imgs = Array.isArray(resp) ? resp : [];
      setItems(
        imgs.map(x => ({
          ...x,
          url:           normalizeMediaUrl(x.media?.url || x.url),
          thumbnail_url: normalizeMediaUrl(x.media?.thumbnail_url || x.thumbnail_url),
          display_url:   normalizeMediaUrl(x.media?.display_url || x.display_url),
        }))
      );
    }catch(e){ setMsg(e.message || 'Не удалось загрузить фото'); }
    finally{ setBusy(false); }
  }, [propertyId, propertyType, busy]);

  React.useEffect(()=>{ load(); }, [propertyId, propertyType]);

  const addFromLibrary = async (picked) => {
    setBusy(true);
    try {
      const hasMain = items.some(x => x.is_main);
      const hasHeroSlider = items.some(x => x.is_hero_slider);
      
      for (let i = 0; i < picked.length; i++) {
        await api.attach(propertyId, {
          media_id: picked[i].id,
          is_main: !hasMain && i === 0,
          is_hero_slider: !hasHeroSlider && i === 0,
          sort_order: (items.length + i),
        });
      }
      await load();
      setMsg(`Добавлено ${picked.length} фото`);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка добавления фото');
    } finally {
      setBusy(false);
    }
  };

  const toggleMain = async (linkId) => {
    setBusy(true);
    try {
      await api.setMain(propertyId, linkId);
      await load();
      setMsg('Главное фото изменено');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка изменения главного фото');
    } finally {
      setBusy(false);
    }
  };

  const toggleFlag = async (linkId, field, value) => {
    setBusy(true);
    try {
      await api.update(propertyId, linkId, { [field]: value });
      await load();
      setMsg(`${field} обновлен`);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      console.error('Error in toggleFlag:', e);
      setMsg(e.message || 'Ошибка изменения флага');
      setTimeout(() => setMsg(''), 3000);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (linkId) => {
    if (!window.confirm('Удалить фото из объекта?')) return;
    setBusy(true);
    try {
      await api.delete(propertyId, linkId);
      await load();
      setMsg('Фото удалено');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка удаления фото');
    } finally {
      setBusy(false);
    }
  };

  const handleDragStart = (e, linkId) => {
    setDragId(linkId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetLinkId) => {
    e.preventDefault();
    if (!dragId || dragId === targetLinkId) return;

    const dragIndex = items.findIndex(x => x.id === dragId);
    const targetIndex = items.findIndex(x => x.id === targetLinkId);
    if (dragIndex === -1 || targetIndex === -1) return;

    const newItems = [...items];
    const [draggedItem] = newItems.splice(dragIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    const newOrder = newItems.map(x => x.id);
    
    setBusy(true);
    try {
      await api.reorder(propertyId, newOrder);
      await load();
    } catch (e) {
      setMsg(e.message || 'Ошибка изменения порядка');
    } finally {
      setBusy(false);
      setDragId(null);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Фотографии ({items.length})</h4>
        <Actions style={{ margin: 0, gap: 8 }}>
          <Btn onClick={() => setOpenPicker(true)} disabled={busy}>
            + Добавить из библиотеки
          </Btn>
          <Btn 
            onClick={async()=>{
              if (!items.length) {
                setMsg('Нет фотографий для удаления');
                setTimeout(() => setMsg(''), 3000);
                return;
              }
              if (!window.confirm(`Удалить ВСЕ фотографии (${items.length} шт.) из галереи объекта? Файлы из медиатеки не удаляются.`)) return;
              setBusy(true);
              setMsg(`Удаление ${items.length} фотографий...`);
              try {
                for (const item of items) {
                  await api.delete(propertyId, item.id);
                }
                await load();
                setMsg('✅ Все фотографии удалены из галереи');
                setTimeout(() => setMsg(''), 3000);
              } catch (e) {
                console.error('Error deleting all photos:', e);
                setMsg(`Ошибка: ${e.message || 'Не удалось удалить все фото'}`);
                setTimeout(() => setMsg(''), 5000);
                await load();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy || items.length === 0}
            style={{background:'#f44336', color:'white'}}
          >
            Удалить все фото
          </Btn>
        </Actions>
      </div>

      {msg && <div style={{ color: msg.includes('Ошибка') ? '#e66' : '#1b5e20', marginBottom: 8 }}>{msg}</div>}

      {busy && <div style={{ opacity: 0.6 }}>Загрузка...</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {items.map((item) => (
          <div
            key={item.id}
            draggable
            onDragStart={(e) => handleDragStart(e, item.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, item.id)}
            style={{
              border: '1px solid var(--bdr)',
              borderRadius: 8,
              overflow: 'hidden',
              cursor: 'move',
              opacity: dragId === item.id ? 0.5 : 1,
            }}
          >
            <div style={{ position: 'relative' }}>
              <img
                src={item.thumbnail_url || item.url}
                alt=""
                style={{ width: '100%', height: 120, objectFit: 'cover' }}
              />
              {item.is_main && (
                <Badge style={{ position: 'absolute', top: 4, left: 4, background: '#1b5e20' }}>
                  Главное
                </Badge>
              )}
              {item.is_hero_slider && (
                <Badge style={{ position: 'absolute', top: 4, right: 4, background: '#1976d2' }}>
                  Слайдер
                </Badge>
              )}
            </div>
            
            <div style={{ padding: 8 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <button
                  type="button"
                  className="btn"
                  style={{ fontSize: 11, padding: '2px 6px' }}
                  onClick={() => toggleMain(item.id)}
                  disabled={busy || item.is_main}
                >
                  {item.is_main ? '✓ Главное' : 'Сделать главным'}
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <button
                  type="button"
                  className="btn"
                  style={{ fontSize: 11, padding: '2px 6px' }}
                  onClick={() => toggleFlag(item.id, 'is_hero_slider', !item.is_hero_slider)}
                  disabled={busy}
                >
                  {item.is_hero_slider ? '✓ В слайдере' : 'В слайдер'}
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ fontSize: 11, padding: '2px 6px' }}
                  onClick={() => toggleFlag(item.id, 'is_active', !item.is_active)}
                  disabled={busy}
                >
                  {item.is_active ? '✓ Активно' : 'Неактивно'}
                </button>
              </div>
              
              <button
                type="button"
                className="btn"
                style={{ fontSize: 11, padding: '2px 6px', color: '#e66' }}
                onClick={() => remove(item.id)}
                disabled={busy}
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && !busy && (
        <div style={{ textAlign: 'center', padding: 20, color: '#777' }}>
          Фотографии не добавлены. Нажмите "Добавить из библиотеки" для добавления.
        </div>
      )}

      {openPicker && (
        <MediaPickerModal
          onClose={() => setOpenPicker(false)}
          onSelect={addFromLibrary}
          multiple={true}
        />
      )}
    </Card>
  );
}